// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useParams } from "react-router-dom";
import { useGetDeal } from "@/hooks/useDeals";
import { FileQuestion, Printer } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";
import PrintableSummarySkeleton from "@/components/deals/skeletons/PrintableSummarySkeleton";
import { LOAN_LABELS, formatDealDateTime, formatInspectionWindow, formatPrintableDate, formatPrintableMoney } from "@/utils/deals.utils";
import { Button } from "@/components/ui/button";
import { DealType, Financing, PurchaseMethod } from "@/enum/common.enum";

/**
 * PrintableDealSummary — a clean one-pager rendering of a deal's contract
 * terms, purpose-built for Print → Save as PDF. Uses @media print to strip
 * chrome (buttons, background) and force letter-sized page margins.
 */

export default function PrintableDealSummary() {
  const { id } = useParams();
  const { data: deal, error } = useGetDeal(id);

  if (error) {
    return <EmptyState icon={FileQuestion} title="Deal not found" description="The deal may have been removed, or you may not have access to it — the link may also be incorrect." className="mx-auto mt-24 max-w-lg" />;
  }
  if (!deal) return <PrintableSummarySkeleton />;

  const ct = deal.contract_terms || {};
  const isCash = ct.purchase_method === PurchaseMethod.CASH || (!ct.purchase_method && deal?.financing === Financing.CASH);
  const generatedAt = new Date().toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" });
  const agent = deal.agent || {};

  const empty =
    !ct.purchase_price && !ct.closing_date && !ct.due_diligence_end_date &&
    !ct.earnest_money_deposit && !ct.financing_contingency_days && !ct.purchase_method &&
    !ct.special_stipulations && !ct.home_sale_contingency_enabled && !ct.kickout_clause_enabled &&
    !ct.closing_address && !ct.closing_datetime && !ct.closing_contact_name && !ct.closing_contact_phone &&
    !ct.earnest_money_holder && !ct.option_fee_amount && !ct.earnest_money_due_date && !ct.option_fee_due_date && !ct.seller_names && !ct.buyer_names &&
    !ct.inspection_date && !ct.inspection_company && !ct.inspection_notes && !ct.closing_law_firm &&
    !ct.listing_agent && !ct.listing_brokerage &&
    !ct.coop_brokerage && !ct.coop_agent && !ct.further_considerations;

  return (
    <div className="min-h-screen bg-linen text-ink font-sans" data-testid="printable-deal-summary">
      {/* Print stylesheet — strips chrome + tightens layout for A4/Letter */}
      <style>{`
        @page { size: letter; margin: 0.5in 0.55in; }
        @media print {
          html, body { background: var(--dash-surface) !important; }
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; margin: 0 !important; box-shadow: none !important; border: none !important; }
          .print-avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
        @media screen {
          .print-page { max-width: 850px; margin: 40px auto; box-shadow: var(--print-page-shadow); }
        }
      `}</style>

      {/* Toolbar — screen only */}
      <div className="no-print sticky top-0 z-10 bg-linen/95 backdrop-blur border-b border-edge">
        <div className="max-w-[850px] mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline text-stone">Print preview</div>
            <div className="text-sm text-ink mt-1">Ready to print or save as PDF from your browser</div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="seamless"
              onClick={() => { if (window.opener) { window.close(); } else { window.history.back(); } }}
              className="text-sm border border-edge px-4 py-2 hover:bg-bone"
              data-testid="print-close-btn"
            >
              Close
            </Button>
            <Button variant="seamless"
              onClick={() => window.print()}
              className="text-sm bg-moss text-linen px-5 py-2 hover:bg-mossDark inline-flex items-center gap-2"
              data-testid="print-now-btn"
            >
              <Printer className="w-3.5 h-3.5" /> Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {/* The page itself */}
      <article className="print-page bg-white px-12 py-14" data-testid="print-canvas">
        {/* Masthead */}
        <header className="flex items-start justify-between border-b border-edge pb-6 mb-10 print-avoid-break">
          <div>
            <img src="/logo.svg" alt="Stoop" className="h-9 w-auto" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone mt-1">run your deals in concert</div>
          </div>
          <div className="text-right">
            <div className="overline text-stone">Deal summary</div>
            <div className="text-sm text-ink mt-1">Generated {generatedAt}</div>
          </div>
        </header>

        {/* Property header */}
        <section className="mb-10 print-avoid-break">
          <div className="overline text-stone">Property · {deal.deal_type === DealType.SELL ? "Listing" : "Buyer rep"} · {deal.phase?.replace(/_/g, " ")}</div>
          <h1 className="font-display text-4xl tracking-tight leading-tight mt-2" data-testid="print-address">{deal.address}</h1>
          <div className="text-stone text-sm mt-3">Client · <span className="text-ink">{deal.client_name}</span></div>
        </section>

        {empty ? (
          <EmptyState compact icon={FileQuestion} title="No contract terms yet" description="Add contract dates and financial details before printing this summary." className="my-8 border-edge" />
        ) : (
          <>
            {/* Money & dates */}
            <Group title="Money & dates" testid="print-money-dates">
              <Grid>
                <Field label="Purchase price"        value={formatPrintableMoney(ct.purchase_price)} />
                <Field label="Earnest money deposit" value={formatPrintableMoney(ct.earnest_money_deposit)} />
                <Field label="Closing date"          value={formatPrintableDate(ct.closing_date)} />
                <Field label="Due diligence ends"    value={formatPrintableDate(ct.due_diligence_end_date)} />
                <Field label="Earnest money held by" value={ct.earnest_money_holder || "—"} />
                <Field label="Option / DD fee"       value={formatPrintableMoney(ct.option_fee_amount)} />
                {ct.earnest_money_due_date && <Field label="Earnest money due" value={formatPrintableDate(ct.earnest_money_due_date)} />}
                {ct.option_fee_due_date && <Field label="DD money due" value={formatPrintableDate(ct.option_fee_due_date)} />}
                <Field label="Inspection date"     value={formatPrintableDate(ct.inspection_date)} />
                <Field label="Inspection company"  value={ct.inspection_company || "—"} />
                {formatInspectionWindow(ct) && <Field label="Inspection window" value={formatInspectionWindow(ct)} />}
                {ct.possession_datetime && <Field label="Possession" value={formatDealDateTime(ct.possession_datetime)} />}
                {ct.utility_connect_date && <Field label="Utilities connect" value={formatPrintableDate(ct.utility_connect_date)} />}
                {ct.utility_disconnect_date && <Field label="Utilities disconnect" value={formatPrintableDate(ct.utility_disconnect_date)} />}
                {ct.inspection_notes && <Field label="Inspection notes" value={ct.inspection_notes} />}
              </Grid>
            </Group>

            {/* Clients */}
            <Group title="Clients" testid="print-parties">
              <Grid>
                <Field label="Seller(s)"        value={ct.seller_names || "—"} />
                <Field label="Buyer(s)"         value={ct.buyer_names || "—"} />
              </Grid>
            </Group>

            {/* The other side */}
            <Group title="The other side" testid="print-other-side">
              <Grid>
                <Field label="Listing agent"            value={ct.listing_agent || "—"} />
                <Field label="Listing brokerage"        value={ct.listing_brokerage || "—"} />
                <Field label="Buyer's agent"            value={ct.coop_agent || "—"} />
                <Field label="Buyer's-agent brokerage"  value={ct.coop_brokerage || "—"} />
              </Grid>
            </Group>

            {/* Way of purchase */}
            <Group title="Way of purchase" testid="print-purchase">
              <Grid>
                <Field label="Payment method"        value={ct.purchase_method ? (isCash ? "Cash" : "Financed") : "—"} />
                <Field label="Loan type"             value={isCash ? "—" : (ct.loan_type ? LOAN_LABELS[ct.loan_type] : "—")} />
                <Field label="Financing contingency" value={isCash ? "—" : (ct.financing_contingency_date ? formatPrintableDate(ct.financing_contingency_date) : ct.financing_contingency_days ? `${ct.financing_contingency_days} days` : "—")} />
              </Grid>
            </Group>

            {/* Contingencies */}
            <Group title="Contingencies" testid="print-contingencies">
              <Grid>
                <Field label="Home-sale contingency" value={ct.home_sale_contingency_enabled ? "Yes" : "No"} />
                <Field label="Kickout clause"        value={ct.kickout_clause_enabled ? "Yes" : "No"} />
              </Grid>
              {ct.home_sale_contingency_enabled && ct.home_sale_contingency_address && (
                <BlockField label="Property that must be sold" value={ct.home_sale_contingency_address} />
              )}
              {ct.kickout_clause_enabled && ct.kickout_clause_details && (
                <BlockField label="Kickout details" value={ct.kickout_clause_details} />
              )}
            </Group>

            {/* Special stipulations */}
            {ct.special_stipulations && (
              <Group title="Special stipulations" testid="print-stipulations">
                <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{ct.special_stipulations}</p>
              </Group>
            )}

            {ct.further_considerations && (
              <Group title="Further considerations" testid="print-further">
                <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{ct.further_considerations}</p>
              </Group>
            )}

            {/* Closing-day logistics */}
            {(ct.closing_address || ct.closing_datetime || ct.closing_contact_name || ct.closing_contact_phone || ct.closing_law_firm) && (
              <Group title="Closing day" testid="print-closing">
                <Grid>
                  {ct.closing_law_firm && <Field label="Law firm / title company" value={ct.closing_law_firm} />}
                  {ct.closing_datetime && (
                    <Field
                      label="When"
                      value={new Date(ct.closing_datetime).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    />
                  )}
                  {ct.closing_contact_name && <Field label="Contact" value={ct.closing_contact_name} />}
                  {ct.closing_contact_phone && <Field label="Phone" value={ct.closing_contact_phone} />}
                </Grid>
                {ct.closing_address && (
                  <BlockField label="Address" value={ct.closing_address} />
                )}
              </Group>
            )}
          </>
        )}

        {/* Footer */}
        <footer className="mt-14 pt-6 border-t border-edge text-xs text-stone print-avoid-break">
          <div className="flex items-start justify-between gap-8">
            <div>
              <div className="overline mb-1">Prepared by</div>
              <div className="text-ink">{agent.name || "—"}</div>
              {agent.email && <div>{agent.email}</div>}
              {agent.phone && <div>{agent.phone}</div>}
            </div>
            <div className="text-right">
              <div className="overline mb-1">Last updated</div>
              <div className="text-ink">{ct.updated_at ? new Date(ct.updated_at).toLocaleDateString(undefined, { dateStyle: "long" }) : "—"}</div>
              {ct.updated_by_name && <div>by {ct.updated_by_name}</div>}
            </div>
          </div>
          <p className="mt-6 text-[11px] leading-relaxed">
            This summary is a non-binding operational reference generated from the deal record on stoop. The signed purchase contract governs all terms in the event of any discrepancy.
          </p>
        </footer>
      </article>
    </div>
  );
}

function Group({ title, testid, children }) {
  return (
    <section className="mb-9 print-avoid-break" data-testid={testid}>
      <h2 className="text-[11px] uppercase tracking-[0.2em] text-stone mb-4 border-b border-edge pb-2">{title}</h2>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">{children}</div>;
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-stone mb-1">{label}</div>
      <div className="font-display text-lg tracking-tight leading-tight text-ink">{value}</div>
    </div>
  );
}

function BlockField({ label, value }) {
  return (
    <div className="mt-5">
      <div className="text-[10px] uppercase tracking-[0.15em] text-stone mb-1">{label}</div>
      <div className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{value}</div>
    </div>
  );
}
